import { each, find, round, startCase } from 'lodash'
import { format } from 'date-fns'
import { capitalizeFirstLetter, getStandardizedName, maybePluralize } from 'common/utils/stringUtils'
import { createUID } from 'common/utils/createUID'
import { CURRENCY_CODE, CURRENCY_DISPLAY, CURRENCY_SYMBOL } from 'common/constants/currencyConstants'
import { MIN_LIFETIME_PLAN_DISCOUNTED_PRICE, MIN_NON_ZERO_DISCOUNTED_PRICE } from '../constants/priceConstants'
import { accessDurationOptions, getAccessDurationOption } from '../components/ProductForm/AccessDurationInput'

export const getProductCurrency = ({ productCurrency, defaultCurrency }) => {
  return (productCurrency || defaultCurrency || CURRENCY_CODE.USD)?.toUpperCase?.()
}

export const showDiscountByType = ({ type, discount, currency }) => {
  if (type === 'trialOnly') {
    return 'None'
  } else if (type === 'fixed') {
    return `${CURRENCY_SYMBOL[currency]}${(discount / 100).toFixed(2)} ${currency}`
  } else {
    return `${discount}%`
  }
}

export const getCouponDurationText = ({ coupon }) => {
  if (coupon.type === 'trialOnly') {
    return !coupon?.trialPeriodDays ? 'Set on plan' : `${startCase(coupon.trialPeriodDays)} days`
  } else if (coupon.duration === 'repeating') {
    return `${coupon.durationInMonths} ${maybePluralize({ count: coupon.durationInMonths, text: 'month' })}`
  } else {
    return startCase(coupon.duration)
  }
}

export const getCouponSuggestionDurationText = ({ coupon }) => {
  if (coupon.type === 'trialOnly') {
    return !coupon?.trialPeriodDays ? 'Set on plan' : `${startCase(coupon.trialPeriodDays)} days`
  } else {
    return startCase(coupon.duration)
  }
}

export const calcDiscountValue = ({ type, price, discount }) => {
  if (type === 'fixed') {
    return discount
  } else {
    const discountCents = (price * discount) / 100
    return round(discountCents / 100, 2) * 100 // convert from cents, round 2 two decimal points and return cents
  }
}

export const calcPriceWithDiscount = ({ type, price, discount }) => {
  if (type === 'trialOnly') {
    return price
  }

  const priceWithDiscount = price - calcDiscountValue({ type, price, discount })
  if (priceWithDiscount < 0) {
    return 0
  }

  return priceWithDiscount
}

export const getUnusedBillingPeriod = (plans) => {
  const periods = { 'month-1': false, 'month-6': false, 'year-1': false, lifetime: false }

  each(plans, (plan) => {
    if (plan?.isLifetimePurchase) {
      periods.lifetime = true
    } else {
      periods[`${plan.time_unit}-${plan.time_val}`] = true
    }
  })

  const firstUnused = find(Object.entries(periods), ([_, isUsed]) => !isUsed)?.at(0) || 'month-1'

  if (firstUnused === 'lifetime') {
    return { unit: 'lifetime', val: 1 }
  } else {
    return { unit: firstUnused.split('-')[0], val: firstUnused.split('-')[1] }
  }
}

export const getDefaultPlanPrice = ({ interval, intervalCount }) => {
  if (interval === 'year') {
    return 15999
  } else if (interval === 'month' && Number(intervalCount) === 6) {
    return 9599
  } else if (interval === 'month' && Number(intervalCount) === 1) {
    return 1999
  } else if (interval === 'lifetime') {
    return 29999
  }
}

export const getDefaultNextPlan = (plans) => {
  const billingPeriod = getUnusedBillingPeriod(plans)
  const defaultPlanPrice = getDefaultPlanPrice({ interval: billingPeriod.unit, intervalCount: billingPeriod.val })

  if (billingPeriod?.unit === 'lifetime') {
    return {
      id: createUID(),
      idx: plans.length,
      price: defaultPlanPrice,
      isLifetimePurchase: true,
      time_unit: 'lifetime',
      time_val: '1',
    }
  } else {
    return {
      id: createUID(),
      idx: plans.length,
      price: defaultPlanPrice,
      time_unit: billingPeriod.unit,
      time_val: Number(billingPeriod.val),
    }
  }
}

export const getPromoPrice = ({ coupon, price }) => {
  if (coupon?.type === 'trialOnly') {
    return price
  }

  const isFree =
    (coupon.type === 'percentage' && coupon.discount === 100) || (coupon.type === 'fixed' && coupon.discount >= price)
  const discountPriceValue = calcDiscountValue({
    type: coupon.type,
    discount: coupon.discount,
    price,
  })
  const priceWithDiscount = price - discountPriceValue

  return isFree ? 0 : priceWithDiscount
}

export const getPlanIntervalText = (plan) => {
  const pluralizedTimeUnit = maybePluralize({ count: plan.time_val, text: plan.time_unit })

  if (plan?.isLifetimePurchase) {
    return 'for lifetime access'
  } else {
    return `billed ${plan.time_val > 1 ? `every ${plan.time_val}` : 'once a'} ${pluralizedTimeUnit}`
  }
}

export const getPlanPromoIntervalText = (plan) => {
  if (plan?.isLifetimePurchase) {
    return 'for lifetime access'
  } else {
    return plan.time_unit === 'month' && plan.time_val === 1 ? 'first month' : 'first payment'
  }
}

export const getPaymentIntervalText = ({ plan }) => {
  const pluralizedTimeUnit = maybePluralize({ count: plan.time_val, text: plan.time_unit })

  if (plan.time_val !== 1) {
    return `every ${plan.time_val} ${pluralizedTimeUnit}`
  } else if (plan.time_unit === 'month') {
    return 'monthly'
  } else {
    return 'anually'
  }
}

export const getPaymentIntervalInMonths = ({ plan }) => {
  if (plan.time_unit === 'month') {
    return Number(plan.time_val)
  } else if (plan.time_unit === 'year') {
    return Number(plan.time_val) * 12
  }
}

export const getPlanPromoBottomText = ({ coupon, plan, currency }) => {
  const isFree =
    (coupon.type === 'percentage' && coupon.discount === 100) ||
    (coupon.type === 'fixed' && coupon.discount >= plan.price)
  const interval = getPlanPromoIntervalText(plan)
  const discountPriceValue = calcDiscountValue({
    type: coupon.type,
    discount: coupon.discount,
    price: plan.price,
  })

  const planPriceText = `${CURRENCY_DISPLAY[currency]}${(plan.price / 100).toFixed(2)}`
  const payIntervalText = getPaymentIntervalText({
    plan,
  })

  if (isFree) {
    if (plan?.isLifetimePurchase) {
      return 'Coupon applied: FREE lifetime access'
    } else {
      if (coupon?.duration === 'forever') {
        return 'Coupon applied: FREE forever'
      } else {
        const paymentIntervalInMonths = getPaymentIntervalInMonths({ plan })
        if (coupon?.duration === 'repeating' && Number(coupon?.durationInMonths) > paymentIntervalInMonths) {
          const couponAppliedTimes = Math.ceil(Number(coupon?.durationInMonths) / paymentIntervalInMonths)
          const freeText = `${couponAppliedTimes * paymentIntervalInMonths} months FREE`
          return `Coupon applied: ${freeText}. ${planPriceText} ${payIntervalText} after.`
        } else {
          const freeText = `${capitalizeFirstLetter(interval)} FREE`
          return `Coupon applied: ${freeText}. ${planPriceText} ${payIntervalText} after.`
        }
      }
    }
  } else {
    const discountPriceText =
      coupon.type === 'percentage'
        ? `${coupon.discount}%`
        : `${CURRENCY_DISPLAY[currency]}${(discountPriceValue / 100).toFixed(2)}`
    if (plan?.isLifetimePurchase) {
      return `Coupon applied: ${discountPriceText} OFF lifetime access. Billed once.`
    } else {
      if (coupon?.duration === 'forever') {
        return `Coupon applied: ${discountPriceText} OFF all future payments. Billed ${payIntervalText}.`
      } else {
        const paymentIntervalInMonths = getPaymentIntervalInMonths({ plan })
        const planPriceText = `${CURRENCY_DISPLAY[currency]}${(plan.price / 100).toFixed(2)}`
        const payAfterDiscountText = `${planPriceText} ${payIntervalText} after`
        if (coupon?.duration === 'repeating' && Number(coupon?.durationInMonths) > paymentIntervalInMonths) {
          const couponAppliedTimes = Math.ceil(Number(coupon?.durationInMonths) / paymentIntervalInMonths)
          const promoText = `${discountPriceText} OFF ${couponAppliedTimes * paymentIntervalInMonths} months`
          return `Coupon applied: ${promoText}. ${payAfterDiscountText}.`
        } else {
          const promoText = `${discountPriceText} OFF first ${interval === 'first month' ? 'month' : 'payment'}`
          return `Coupon applied: ${promoText}. ${payAfterDiscountText}.`
        }
      }
    }
  }
}

export const getPromoText = ({ activeCoupon, plan, currency }) => {
  if (activeCoupon && activeCoupon?.type !== 'trialOnly') {
    return getPlanPromoBottomText({ coupon: activeCoupon, plan, currency })
  } else {
    return plan.promoText
  }
}

export const getTrialText = ({ activeCoupon, plan }) => {
  if (plan?.isLifetimePurchase) {
    return null
  } else if (activeCoupon?.type === 'trialOnly' && activeCoupon?.trialPeriodDays) {
    //For styling, only show Coupon applied text only if no dollar discount is also applied
    return `Coupon applied: ${activeCoupon?.trialPeriodDays} day free trial.`
  } else if (activeCoupon?.trialPeriodDays || plan?.trialPeriodDays) {
    return `${activeCoupon?.trialPeriodDays || plan.trialPeriodDays} day free trial.`
  } else {
    return null
  }
}

export const getIsValidDiscountedPrice = (discountedPrice) =>
  discountedPrice === 0 || discountedPrice >= MIN_NON_ZERO_DISCOUNTED_PRICE

export const getIsValidProductDiscount = ({ product, coupon }) => {
  const discountedPrice = getPromoPrice({ price: product?.price, coupon })
  const productCurrency = getStandardizedName(product?.currency || CURRENCY_CODE.USD)
  const couponCurrency = getStandardizedName(coupon?.currency || CURRENCY_CODE.USD)
  if (!getIsValidDiscountedPrice(discountedPrice) || coupon?.type === 'trialOnly') {
    return false
  } else if (coupon?.stripeStatus === 'pending' || coupon?.stripeStatus === 'error') {
    return false
  } else if (productCurrency !== couponCurrency && coupon?.type === 'fixed') {
    return false
  } else {
    return true
  }
}

export const getIsValidPlanDiscount = ({ plan, coupon }) => {
  const discountedPrice = getPromoPrice({ price: plan?.price, coupon })
  const planCurrency = getStandardizedName(plan?.currency || CURRENCY_CODE.USD)
  const couponCurrency = getStandardizedName(coupon?.currency || CURRENCY_CODE.USD)

  if (
    plan?.isLifetimePurchase &&
    (discountedPrice < MIN_LIFETIME_PLAN_DISCOUNTED_PRICE || coupon?.type === 'trialOnly')
  ) {
    return false
  } else if (!plan?.isLifetimePurchase && !getIsValidDiscountedPrice(discountedPrice)) {
    return false
  } else if (coupon?.stripeStatus === 'pending' || coupon?.stripeStatus === 'error') {
    return false
  } else if (planCurrency !== couponCurrency && coupon?.type === 'fixed') {
    return false
  } else {
    return true
  }
}

export const getIsAlwaysActive = ({ promo, coupon }) => {
  // promo values (alwaysActive) are legacy.
  // These values have been migrated to coupon, so coupon
  // should always take priority over promo.
  const alwaysActive = typeof coupon?.alwaysActive !== 'undefined' ? coupon?.alwaysActive : Boolean(promo?.alwaysActive)
  return alwaysActive
}

export const getIsCouponActive = ({ promo, coupon }) => {
  // promo values (startDate, expiration) are legacy.
  // These values have been migrated to coupon, so coupon
  // should always take priority over promo.
  const alwaysActive = getIsAlwaysActive({ promo, coupon })
  if (alwaysActive) return true

  const startTime = typeof coupon?.startDate !== 'undefined' ? coupon?.startDate : promo?.startDate
  if (!startTime) return false

  const now = Date.now()
  const hasStarted = now >= startTime
  const expirationTime = typeof coupon?.expiration !== 'undefined' ? coupon?.expiration : promo?.expiration
  const hasNotExpired = !expirationTime || now < expirationTime
  return hasStarted && hasNotExpired
}

export const getIsCouponUpcoming = ({ promo, coupon }) => {
  // promo values (startDate) are legacy.
  // These values have been migrated to coupon, so coupon
  // should always take priority over promo.
  const alwaysActive = getIsAlwaysActive({ promo, coupon })
  if (alwaysActive) return false

  const startTime = typeof coupon?.startDate !== 'undefined' ? coupon?.startDate : promo?.startDate
  if (!startTime) return false

  const now = Date.now()
  return startTime > now
}

export const getAppliedPromo = ({ product, coupons }) => {
  let appliedPromo = null
  if (product?.promo) {
    appliedPromo = product?.promo?.find?.(
      (prm) => getIsCouponActive({ promo: prm, coupon: coupons?.[prm?.stripeCouponId] }) && prm.isAutoApplied
    )
  }
  return appliedPromo
}

export const getProductDurationText = ({ accessEnd, accessUnit, accessValue }) => {
  const accessDuration = getAccessDurationOption({
    accessEnd,
    accessUnit,
  })
  if (accessDuration === accessDurationOptions.forever) {
    return 'Lifetime access'
  } else if (accessDuration === accessDurationOptions.period) {
    return `Access for ${accessValue} ${maybePluralize({ count: accessValue, text: accessUnit })}`
  } else if (accessDuration === accessDurationOptions.date) {
    return `Exclusive access until challenge ends on ${format(accessEnd, 'MMM do, yyyy')}`
  }
}
